import { IMediaAssetModel, mediaAspectRatio, photoDisplayUrl } from "../Services/MediaAssetService";

const INLINE_IMAGE_SELECTOR = "img[data-asset-uuid]";
const ASSET_UUID_ATTRIBUTE = "data-asset-uuid";

/** What the stored HTML carries as `src` of an inline image: a 1x1 transparent GIF. Quill drops an `<img>`
 *  without a `src` when it parses the HTML, so the attribute cannot simply be left out. */
export const INLINE_IMAGE_PLACEHOLDER_SRC = "data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==";

/**
 * An inline image in a text section (inserted through the WYSIWYG editor's image button) is stored as
 * `<img data-asset-uuid="...">` — never with a real URL, because a signed bucket URL expires within the
 * hour. Whoever renders such HTML calls this afterwards to point every image at the URL the article's media
 * list currently holds for it. No request is made here: the URLs come with that list.
 */
export const resolveInlineMediaImages = (container: HTMLElement | null | undefined, mediaAssets: IMediaAssetModel[]): void => {
    if (!container) {
        return;
    }
    container.querySelectorAll<HTMLImageElement>(INLINE_IMAGE_SELECTOR).forEach((image) => {
        const assetUuid = image.getAttribute(ASSET_UUID_ATTRIBUTE);
        const asset = mediaAssets.find((candidate) => candidate.uuid === assetUuid);
        const url = asset ? photoDisplayUrl(asset) : undefined;
        if (url && image.getAttribute("src") !== url) {
            image.setAttribute("src", url);
        }
        // Its own shape, so the picture fills the box the stylesheet reserved instead of sitting in a
        // default one. Left alone for an image whose resolution was never recorded.
        const ratio = asset ? mediaAspectRatio(asset) : undefined;
        if (ratio) {
            image.style.aspectRatio = String(ratio);
        }
    });
};

const INLINE_IMAGE_TAG_PATTERN = /<img\b[^>]*\bdata-asset-uuid="[^"]*"[^>]*>/gi;
const SRC_ATTRIBUTE_PATTERN = /(\s)src="[^"]*"/i;

/**
 * The reverse, for HTML on its way to being saved: every inline image gets the placeholder `src` back, so a
 * signed URL never ends up in the stored content — and the content only changes when the author changed it,
 * not because a URL was refreshed. Deliberately a text replacement: re-parsing the editor's HTML would move
 * the helper elements its table module keeps inside a `<table>`.
 */
export const stripInlineMediaImageSources = (html: string): string =>
    html.replace(INLINE_IMAGE_TAG_PATTERN, (tag) =>
        SRC_ATTRIBUTE_PATTERN.test(tag)
            ? tag.replace(SRC_ATTRIBUTE_PATTERN, `$1src="${INLINE_IMAGE_PLACEHOLDER_SRC}"`)
            : tag.replace(/<img\b/i, `<img src="${INLINE_IMAGE_PLACEHOLDER_SRC}"`)
    );
